import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../../middleware/api/apiList'
import { AppDispatch, RootState } from '../../../../stateStore'
import { LoadingState, ValidationError } from '../../../../types/commonTypes'
import editProjectCreationRequestApi from '../../../../middleware/api/ProjectManagement/ProjectCreationRequests/EditProjectCreationRequestApi'
import {
  Chelist,
  EditProjectRequestDetails,
  GetProjectRequestMailIds,
  GetProjectRequestState,
} from '../../../../types/ProjectManagement/ProjectCreationRequests/EditProjectRequest/editProjectRequestTypes'

const getCheckList = createAsyncThunk(
  'editProjectCreationRequest/getCheckList',
  async (_, thunkApi) => {
    try {
      return await editProjectCreationRequestApi.getEditCheckList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getProjectRequestMailIds = createAsyncThunk(
  'editProjectCreationRequest/getProjectRequestMailIds',
  async (_, thunkApi) => {
    try {
      return await editProjectCreationRequestApi.getEditProjectRequestMailIds()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editProjectRequest = createAsyncThunk<
  number | undefined,
  EditProjectRequestDetails,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'editProjectCreationRequest/editProjectRequest',
  async (editProjectRequestDetails: EditProjectRequestDetails, thunkApi) => {
    try {
      return await editProjectCreationRequestApi.editProjectRequest(
        editProjectRequestDetails,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialEditProjectCreationRequestState: GetProjectRequestState = {
  getProjectRequestMailIds: {} as GetProjectRequestMailIds,
  chelist: [],
  isLoading: ApiLoadingState.idle,
}

const editProjectCreationRequestSlice = createSlice({
  name: 'editProjectCreationRequest',
  initialState: initialEditProjectCreationRequestState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCheckList.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.chelist = action.payload
    })
    builder
      .addCase(getProjectRequestMailIds.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getProjectRequestMailIds = action.payload
      })
      .addMatcher(
        isAnyOf(getCheckList.pending, getProjectRequestMailIds.pending),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.editProjectCreationRequest.isLoading

const checkList = (state: RootState): Chelist[] =>
  state.editProjectCreationRequest.chelist

const projectRequestMailIds = (state: RootState): GetProjectRequestMailIds =>
  state.editProjectCreationRequest.getProjectRequestMailIds

const editProjectCreationRequestSelectors = {
  isLoading,
  checkList,
  projectRequestMailIds,
}

const editProjectCreationRequestThunk = {
  getCheckList,
  getProjectRequestMailIds,
  editProjectRequest,
}

export const editProjectCreationRequestService = {
  ...editProjectCreationRequestThunk,
  actions: editProjectCreationRequestSlice.actions,
  selectors: editProjectCreationRequestSelectors,
}

export default editProjectCreationRequestSlice.reducer
